<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useIdeStore } from '@/stores/ide.store'
import Button from '@/components/atoms/button/index.vue'
import First from '@/assets/images/ide/embed-share/First.svg'
import FirstDark from '@/assets/images/ide/embed-share/First-dark.svg'
import Second from '@/assets/images/ide/embed-share/Second.svg'
import SecondDark from '@/assets/images/ide/embed-share/Second-dark.svg'
import Step1Light from '@/assets/images/ide/embed-share/step-1-copy.svg?component'
import Step1Dark from '@/assets/images/ide/embed-share/step-1-copy-dark.svg?component'
import Step2Light from '@/assets/images/ide/embed-share/step-2-copy.svg?component'
import Step2Dark from '@/assets/images/ide/embed-share/step-2-copy-dark.svg?component'
import embedConfigureService from '@/services/embeds/embedConfigure.service'

const protocol = window.location.protocol
const host = window.location.hostname

const ideStore = useIdeStore()

let intervalId: NodeJS.Timeout | null = null

const embedKey = ref<string>('')
const copied = ref<boolean>(false)

const isDark = computed(() => ideStore?.colorMode === 'dark')

const Step1 = computed(() => (isDark.value ? Step1Dark : Step1Light))
const Step2 = computed(() => (isDark.value ? Step2Dark : Step2Light))

const defaultSlides = [
  {
    id: 1,
    title: 'Integrate our code editor into a documentation page, blog, or website.',
    image: First
  },
  {
    id: 2,
    title:
      'Add a <strong>functional IDE</strong> into your platform with just <strong>three lines of HTML code</strong>.',
    image: Second
  }
]
const slides = ref([...defaultSlides])
const showManageEmbed = ref<boolean>(false)

/**
 * @param index of the slide in defaultSlides
 * @param clearInterval of auto slide
 */
const goToSlide = (index: number, clearInterval: boolean = true) => {
  const slideToMove = defaultSlides[index]
  const slideIndex = slides.value.findIndex((slide) => slide.id === slideToMove.id)

  if (slideIndex !== -1) {
    const [activeSlide] = slides.value.splice(slideIndex, 1)
    slides.value.unshift(activeSlide)
  }

  if (clearInterval) {
    clearSlidesInterval()
  }
}

/**
 * @param show manage embed view
 */
const setShowManageEmbed = (show: boolean) => {
  showManageEmbed.value = show
}

/**
 * Show next embed view
 */
const handleEmbed = async () => {
  if (!showManageEmbed.value) {
    const generatedEmbedKey = await embedConfigureService.initEmbedConfigure()
    embedKey.value = generatedEmbedKey
    setShowManageEmbed(true)
  } else {
    window.open('/embed-manager', '_blank')
  }
}

/**
 * Clear slides interval
 */
const clearSlidesInterval = () => {
  if (intervalId) clearInterval(intervalId)
}

/**
 * Copy embed code to clip board
 */
const handleCopy = () => {
  const textarea = document.getElementById('embed-share-code') as HTMLTextAreaElement
  if (textarea?.value) window.navigator.clipboard.writeText(textarea?.value)
  copied.value = true

  setTimeout(() => {
    copied.value = false
  }, 1000)
}

onMounted(() => {
  intervalId = setInterval(() => {
    goToSlide(slides.value[0].id === 1 ? 1 : 0, false)
  }, 5000)

  onUnmounted(() => {
    clearSlidesInterval()
  })
})

watch(
  isDark,
  (dark) => {
    slides.value = defaultSlides.map((slide) => ({
      ...slide,
      image: dark ? (slide.id === 1 ? FirstDark : SecondDark) : slide.id === 1 ? First : Second
    }))
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="!showManageEmbed">
    <div class="carousel-view">
      <transition-group class="carousel" tag="div">
        <div v-for="slide in slides" class="slide" :key="slide.id">
          <!-- Slide Text -->
          <div class="text-high text-center text-sm">
            <h2 v-html="slide.title"></h2>
          </div>

          <!-- Slide Image -->
          <div class="carousel-image py-4">
            <img
              :src="slide.image"
              alt="Slide Image"
              draggable="false"
              @mouseup="goToSlide(slide.id === 1 ? 1 : 0)"
              @touchend="goToSlide(slide.id === 1 ? 1 : 0)"
            />
          </div>
        </div>
      </transition-group>

      <!-- Navigation Dots -->
      <div class="carousel-dots">
        <span
          v-for="(slide, index) in defaultSlides"
          :key="index"
          @click="goToSlide(index)"
          class="dot"
          :class="{ active: slides[0].id === slide.id }"
        ></span>
      </div>
    </div>
  </div>
  <div v-else>
    <!-- <BaseTextbox label="Basic embed code"  /> -->
    <label class="p-small text-high my-3 block font-semibold">Basic embed code</label>
    <div class="relative">
      <textarea
        id="embed-share-code"
        rows="4"
        disabled
        :value="`  <body>
      <div data-pym-src='${protocol}//${host}/embed/v1/${embedKey}'></div>
      <script src='${protocol}//${host}/assets/jdoodle-pym.min.js' type='text/javascript'></script>
  </body>`"
        class="background-primary stroke-primary-default mb-4 w-full rounded-lg border p-1"
      />

      <button class="absolute right-2 top-2" @click="handleCopy">
        <FontAwesomeIcon icon="fa-copy" class="text-high mr-2 h-4 w-3 cursor-pointer" />
        {{ copied ? 'Copied' : 'Copy' }}
      </button>
    </div>

    <div class="surface-primary-header rounded-lg p-3">
      <h3 class="text-high mb-3 text-sm font-semibold">
        Insert the above embed code inside your HTML pages in 2 easy steps
      </h3>

      <div class="mx-auto flex w-full flex-col sm:flex-row sm:gap-4 md:w-11/12 lg:w-3/4">
        <div class="w-full py-4 text-center text-sm">
          <b>Step 1: </b>
          <p class="inline-block">Copy the generated code</p>
          <Step1 class="mx-auto mt-3" />
        </div>
        <div class="w-full py-4 text-center text-sm">
          <b>Step 2: </b>
          <p class="inline-block">Paste code inside HTML tag</p>
          <Step2 class="mx-auto mt-3" />
        </div>
      </div>
    </div>
  </div>

  <div class="flex items-center justify-between gap-x-2 pt-4">
    <a href="/docs/embeds/introduction-to-embeds" target="_blank">
      <Button type="button" variant="link" color="secondary"> View documentation </Button>
    </a>
    <Button type="button" variant="outline" @click="handleEmbed">
      {{ !showManageEmbed ? 'Generate Basic Embed' : 'Manage Embed' }}
    </Button>
  </div>
</template>

<style scoped>
.carousel-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  width: auto;
  margin: auto;
  user-select: none;
}

.carousel-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
}

.carousel {
  display: flex;
  flex-direction: row;
}

.slide {
  flex: 1;
  min-width: 100%;
  transition: transform 0.3s ease-in-out;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.dot {
  height: 6px;
  width: 6px;
  margin: 0 2px;
  border-radius: 4px;
  display: inline-block;
  cursor: pointer;
  transition: width 0.3s ease-in-out;
  @apply bg-new-secondary-300 dark:bg-new-neutral-700;
}

.dot.active {
  width: 18px;
  @apply bg-new-secondary-500 dark:bg-new-neutral-500;
}
</style>
